import * as React from "react"
import { cn } from "../../lib/utils"

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'default' | 'outline' | 'ghost'
  size?: 'default' | 'sm' | 'lg' | 'icon'
  responsive?: boolean
  /** Use on auth/form pages: h-12 on mobile, h-9 on md+ */
  mobileFullHeight?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'default', size = 'default', responsive = false, mobileFullHeight = false, ...props }, ref) => {
    return (
      <button
        className={cn(
          "inline-flex items-center justify-center rounded-md font-medium font-mono uppercase tracking-wide transition-colors focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50",
          {
            'bg-primary text-primary-foreground hover:bg-primary/90': variant === 'default',
            'border border-input bg-background hover:bg-white hover:border-border/40 dark:hover:bg-accent dark:hover:text-accent-foreground': variant === 'outline',
            'border border-transparent hover:bg-white hover:border-border/40 hover:text-accent-foreground dark:hover:bg-accent dark:hover:border-transparent': variant === 'ghost',
            // Responsive sizing
            'h-9 px-3 py-2 text-sm md:h-10 md:px-4 md:text-base': size === 'default' && responsive,
            'h-8 px-2.5 text-sm md:h-9 md:px-3': size === 'sm' && responsive,
            'h-10 px-5 text-sm md:h-11 md:px-8 md:text-base': size === 'lg' && responsive,
            // Non-responsive sizing (default)
            'h-8 max-h-8 px-4 py-2': size === 'default' && !responsive && !mobileFullHeight,
            'h-8 max-h-8 rounded-md px-3': size === 'sm' && !responsive && !mobileFullHeight,
            'h-8 max-h-8 rounded-md px-8': size === 'lg' && !responsive && !mobileFullHeight,
            'h-8 max-h-8 w-8': size === 'icon',
            // Mobile-friendly height: 48px on mobile, 36px on md+
            'h-12 md:h-9 px-4 text-sm': mobileFullHeight && size === 'default',
            'h-12 md:h-9 px-8 text-sm': mobileFullHeight && size === 'lg',
            'h-12 md:h-9 px-3 text-sm': mobileFullHeight && size === 'sm',
          },
          className
        )}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button }
